import styled from "styled-components"

import { Switch, Text } from "@minuthq/meatball-ui-react"

import { UpgradeIconButton } from "src/components/FeatureBlockers/UpgradeIconButton"
import { HREF_MINUT_HELP_PRESENCE_DETECTION } from "src/constants/hrefs"
import { useFeatureAvailability } from "src/data/featureAvailability/logic/useFeatureAvailability"
import { THome } from "src/data/homes/types/homeTypes"
import { usePatchOccupancy } from "src/data/occupancy/queries/occupancyQueries"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { usePatchHomeMotionAutomation } from "src/data/organizations/queries/homeQueries"
import { langKeys } from "src/i18n/langKeys"
import { useTranslate } from "src/i18n/useTranslate"
import { ExternalLink } from "src/ui/Link/ExternalLink"
import { spacing } from "src/ui/spacing"

export function PresenceAndCrowdDetectSettings({
  homeData,
}: {
  homeData: THome
}) {
  const { org } = useOrganization()
  const patchHomeMotionAutomation = usePatchHomeMotionAutomation({
    orgId: org.id,
  })
  const patchOccupancy = usePatchOccupancy({
    orgId: org?.id,
    homeId: homeData.home_id,
  })
  const { t } = useTranslate()

  const { available: motionAutomationAvailable, required_plan_types } =
    useFeatureAvailability({
      feature: "motion_automation",
    })

  function handlePresenceDetectionChange(checked: boolean) {
    patchHomeMotionAutomation.mutate({
      path: { home_id: homeData.home_id },
      body: { mode: checked ? "enabled" : "disabled" },
    })
  }

  function handleCrowdDetectChange(checked: boolean) {
    patchOccupancy.mutate({
      active: checked,
      capacity: homeData.occupancy?.capacity,
    })
  }
  return (
    <PresenceSettingsBox>
      <Box>
        <PresenceSettingsItemText>
          <UpgradeWrapper>
            <Text variant="subtitleS">{t(langKeys.motion_detection)}</Text>
            <UpgradeIconButton
              context="motion_automation"
              requiredPlan={required_plan_types?.[0]}
              hidden={!!motionAutomationAvailable}
            />
          </UpgradeWrapper>

          <Text variant="bodyS" color="tertiary" textAlign="left">
            {t(langKeys.motion_detection_short_description)}{" "}
            <ExternalLink
              href={HREF_MINUT_HELP_PRESENCE_DETECTION}
              showExternalIcon={false}
            >
              {t(langKeys.learn_more)}
            </ExternalLink>
          </Text>
        </PresenceSettingsItemText>
        <Switch
          checked={homeData.motion_automation?.mode === "enabled"}
          onChange={handlePresenceDetectionChange}
          disabled={
            patchHomeMotionAutomation.isLoading || !motionAutomationAvailable
          }
        />
      </Box>
      <Box>
        <PresenceSettingsItemText>
          <Text variant="subtitleS">{t(langKeys.crowd_detect_title)}</Text>
          <Text variant="bodyS" color="tertiary" textAlign="left">
            {t(langKeys.crowd_detect_short_description)}
          </Text>
        </PresenceSettingsItemText>
        <Switch
          checked={homeData.occupancy?.active}
          onChange={handleCrowdDetectChange}
          disabled={patchOccupancy.isLoading}
        />
      </Box>
    </PresenceSettingsBox>
  )
}
const PresenceSettingsBox = styled.div`
  display: grid;
  gap: ${spacing.M};
`

const Box = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: ${spacing.L};
  align-items: center;
`
const PresenceSettingsItemText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const UpgradeWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${spacing.XS};
`
